import { defineStore } from 'pinia'
import IndexedDBStorage from "./indexedDBStorage"
import { toRaw } from 'vue'
import AppSyncService from "../services/appSyncService"

const name = 'cache'
const storage = new IndexedDBStorage()

// Le cache synchronisé compte ~11 000 entrées et ~190 Mo de JSON : le décompresser en entier au démarrage
// prenait plusieurs secondes et des centaines de Mo de mémoire. Seul l'index (métadonnées) est chargé ;
// les données sont lues dans IndexedDB à la demande. Index et entrées récentes restent hors de l'état Pinia
// (pas de proxy Vue) ; l'état ne porte que `indexVersion` pour que les getters suivent les changements.
const index = new Map()       // key → { timestamp, ttl, method, target }
const recent = new Map()      // dernières entrées lues ou écrites (ordre d'insertion = LRU)
const RECENT_MAX = 100
let initPromise = null
let generation = 0            // incrémenté par clear() : une reconstruction en cours s'arrête

// Seules les lectures vont dans le cache (et sont servies hors ligne) : tout GET, et les POST dont l'action est
// detail, list…, find…, load…, search… ou timeline — ce que la synchronisation enregistre. Une écriture (create,
// update…, delete, add…, remove…, signin, updateGeo, upload, identify, sendMail…) n'y est jamais relue : chaque
// position GPS ou modification ajoutait une entrée, et la connexion y laissait le mot de passe en clair.
const READ_POST = /\/(detail|list\w*|find\w*|load\w*|search\w*|timeline)$/i

export function isCacheableRequest(method, target) {
    if (String(method).toUpperCase() === 'GET') return true
    return READ_POST.test(String(target || '').split('?')[0]) && target !== '/appSync/loadEntries'
}

function toMeta(entry) {
    return { timestamp: entry.timestamp, ttl: entry.ttl, method: entry.method, target: entry.target }
}

function isFresh(meta, now = Date.now()) {
    return now - meta.timestamp <= meta.ttl
}

function remember(key, entry) {
    recent.delete(key)
    recent.set(key, entry)
    if (recent.size > RECENT_MAX) recent.delete(recent.keys().next().value)
}

export const useCacheStore = defineStore(name, {
    id: name,

    state: () => ({
        indexVersion: 0,
        // Faux tant que l'index d'une base créée avant le store meta est en reconstruction :
        // une clé absente de l'index peut alors exister dans IndexedDB
        indexComplete: false,
        defaultTTL: 24 * 60 * 60 * 1000, // 24h
        isInitialized: false,
        isInitializing: false,
        cachedStorageInfo: null,
        sync: {
            isActive: false,
            message: '',
            progress: 0,
            totalItems: 0,
            completedItems: 0,
            errors: 0,
            startTime: null,
            status: 'idle' // 'idle', 'running', 'completed', 'error'
        }
    }),

    actions: {
        // Tous les appelants attendent la même promesse : avant, un second appel pendant le chargement
        // passait isInitialized à true sans attendre
        init() {
            if (!initPromise) initPromise = this.loadIndex()
            return initPromise
        },

        async loadIndex() {
            const startTime = performance.now()
            this.isInitializing = true
            this.updateStorageInfo()
            try {
                const [metas, keys] = await Promise.all([storage.getAllMeta(), storage.keys()])
                index.clear()
                for (const [key, meta] of metas) index.set(key, meta)

                const missing = keys.filter(key => !index.has(key))
                this.indexComplete = missing.length === 0
                if (missing.length > 0) {
                    // Base créée avant le store meta : reconstruire l'index en tâche de fond,
                    // les lectures passent par IndexedDB en attendant
                    setTimeout(() => this.rebuildIndex(missing), 2000)
                } else {
                    setTimeout(() => this.purgeWrites(), 2000)
                }
            } catch (error) {
                console.error('Cache index loading failed:', error)
                index.clear()
                this.indexComplete = false
            } finally {
                this.indexVersion++
                this.isInitializing = false
                this.isInitialized = true
            }
            console.log(`Cache initialized: ${index.size} entries in ${Math.round(performance.now() - startTime)} ms (t=${Math.round(performance.now())} ms)`)
        },

        async rebuildIndex(keys) {
            const startTime = performance.now()
            const startGeneration = generation
            const BATCH_SIZE = 50
            console.log(`Cache index rebuild: ${keys.length} entries...`)
            try {
                for (let i = 0; i < keys.length; i += BATCH_SIZE) {
                    const entries = await storage.getMultiple(keys.slice(i, i + BATCH_SIZE))
                    if (generation !== startGeneration) return
                    const metas = []
                    for (const [key, value] of entries) {
                        if (value?.timestamp === undefined) continue
                        const meta = toMeta(value)
                        metas.push([key, meta])
                        // Une écriture plus récente a pu passer entre-temps
                        if (!index.has(key)) index.set(key, meta)
                    }
                    await storage.setMetaBulk(metas)
                    await new Promise(resolve => setTimeout(resolve, 0))
                }
                this.indexComplete = true
                console.log(`Cache index rebuilt in ${Math.round(performance.now() - startTime)} ms`)
                await this.purgeWrites()
            } catch (error) {
                console.error('Cache index rebuild failed:', error)
            } finally {
                this.indexVersion++
            }
        },

        // Entrées d'écritures enregistrées par les versions précédentes (voir isCacheableRequest) : supprimées
        async purgeWrites() {
            const keys = [...index.entries()].filter(([, meta]) => !isCacheableRequest(meta.method, meta.target)).map(([key]) => key)
            if (!keys.length) return
            for (const key of keys) {
                try {
                    await storage.removeItem(key)
                    index.delete(key)
                    recent.delete(key)
                } catch (error) {
                    console.warn(`Cache purge failed for ${key}:`, error.message)
                }
            }
            this.indexVersion++
            console.log(`Cache: ${keys.length} écriture(s) retirée(s) du cache`)
        },

        // Entrée complète (données comprises) : entrées récentes, sinon IndexedDB
        async readEntry(key) {
            await this.init()

            const meta = index.get(key)
            if (!meta && this.indexComplete) return null

            let entry = recent.get(key)
            if (entry) {
                remember(key, entry)
                return entry
            }

            try {
                entry = await storage.getItem(key)
            } catch (error) {
                console.warn(`Failed to load cache entry ${key}:`, error.message)
                return null
            }
            if (entry?.data === undefined) return null
            remember(key, entry)
            if (!meta) {
                index.set(key, toMeta(entry))
                this.indexVersion++
            }
            return entry
        },

        startSync() {
            this.sync = {
                isActive: true,
                message: 'Initialisation...',
                progress: 0,
                completedItems: 0,
                errors: 0,
                startTime: Date.now(),
                status: 'running'
            }
        },

        updateSyncProgress(completedItems, message, totalItems = null, progress = null) {
            this.sync.completedItems = completedItems;
            this.sync.message = message;

            if (totalItems !== null) {
                this.sync.totalItems = totalItems;
            }

            if (progress !== null) {
                this.sync.progress = progress;
            } else if (this.sync.totalItems > 0) {
                this.sync.progress = Math.floor((completedItems / this.sync.totalItems) * 100);
            }
        },

        updateSyncTotalItems(totalItems, message) {
            this.sync.totalItems = totalItems;
            this.sync.currentService = message;
            this.sync.progress = 0;
        },

        updateSyncCompletedItems(completedItems) {
            if (this.sync.isActive) {
                this.sync.completedItems = completedItems

                // Auto-calculate progress if not manually set
                if (this.sync.totalItems > 0) {
                    const calculatedProgress = Math.floor((completedItems / this.sync.totalItems) * 100)
                    // Use max of manual progress or calculated progress
                    this.sync.progress = Math.max(this.sync.progress, calculatedProgress)
                }
            }
        },

        completeSyncSuccess(totalProcessed) {
            this.sync.isActive = false
            this.sync.status = 'completed'
            this.sync.progress = 100
            this.sync.completedItems = totalProcessed
            this.sync.message = 'Synchronisation terminée'

            console.log(`Sync completed: ${totalProcessed} items processed`)
        },

        completeSyncError(errorMessage) {
            this.sync.isActive = false
            this.sync.status = 'error'
            this.sync.errors++
            this.sync.message = `Erreur: ${errorMessage}`

            console.error(`Sync failed: ${errorMessage}`)
        },

        resetSync() {
            this.sync = {
                isActive: false,
                message: '',
                progress: 0,
                totalItems: 0,
                completedItems: 0,
                errors: 0,
                startTime: null,
                status: 'idle'
            }
        },

        generateKey(method, target, payload = null) {
            const keyData = { method, target }
            if (['POST', 'PUT', 'PATCH'].includes(method.toUpperCase()) && payload) {
                keyData.payload = payload
            }
            return this.simpleHash(JSON.stringify(keyData))
        },

        simpleHash(str) {
            let hash = 0
            for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) - hash) + str.charCodeAt(i)
                hash = hash & hash
            }
            return Math.abs(hash).toString(36)
        },

        async set(method, target, params, response, ttl = null, payload = null) {
            if (!isCacheableRequest(method, target)) return
            await this.init()

            const key = this.generateKey(method, target, payload)
            const entry = {
                data: toRaw(response).data,
                status: response.status || 200,
                timestamp: Date.now(),
                ttl: ttl || this.defaultTTL,
                method,
                target,
                params,
                payload: payload || null
            }

            storage.setItem(key, entry, toMeta(entry))
                .then(() => console.log(`Cache entry ${key} stored`))
                .catch(error => console.warn(`Failed to persist cache entry ${key}:`, error.message))

            index.set(key, toMeta(entry))
            remember(key, entry)
            this.indexVersion++
        },

        async setBulk(entries) {
            await this.init()

            if (entries.length === 0) return

            const startTime = performance.now()
            const INDEXEDDB_CHUNK_SIZE = 100 // Write to IndexedDB in chunks

            // Prepare all entries at once - no intermediate updates
            const storageEntries = []
            let memoryUpdates = 0
            let skippedEntries = 0

            for (const entry of entries) {
                try {
                    const { method, target, params, response, ttl, payload } = entry

                    if (!method || !target || !response?.data) {
                        skippedEntries++
                        continue
                    }

                    const key = this.generateKey(method, target, payload)
                    const cacheEntry = {
                        data: toRaw(response).data,
                        status: response.status || 200,
                        timestamp: Date.now(),
                        ttl: ttl || this.defaultTTL,
                        method,
                        target,
                        params: params || {},
                        payload: payload || null
                    }

                    const meta = toMeta(cacheEntry)
                    index.set(key, meta)
                    memoryUpdates++
                    storageEntries.push([key, cacheEntry, meta])

                } catch (error) {
                    console.warn('Error preparing bulk entry:', error.message)
                    skippedEntries++
                }
            }

            try {
                // Write to IndexedDB in chunks to avoid blocking
                let successCount = 0

                for (let i = 0; i < storageEntries.length; i += INDEXEDDB_CHUNK_SIZE) {
                    const chunk = storageEntries.slice(i, i + INDEXEDDB_CHUNK_SIZE)
                    const results = await storage.setBulk(chunk)
                    successCount += results.reduce((sum, r) => sum + (r.success ? r.count : 0), 0)

                    // Update progress during IndexedDB writes
                    if (this.sync.isActive) {
                        this.sync.completedItems = successCount
                        this.sync.progress = Math.floor((successCount / this.sync.totalItems) * 100)
                    }

                    // Allow UI to breathe between IndexedDB writes
                    if (i + INDEXEDDB_CHUNK_SIZE < storageEntries.length) {
                        await new Promise(resolve => setTimeout(resolve, 0))
                    }
                }

                this.indexVersion++
                const duration = performance.now() - startTime

                console.log(`Bulk cache: ${successCount}/${entries.length} in ${Math.round(duration)}ms (${Math.round(successCount / (duration / 1000))} entries/sec)`)

                return {
                    success: true,
                    totalEntries: entries.length,
                    storedEntries: successCount,
                    memoryEntries: memoryUpdates,
                    skippedEntries,
                    duration
                }

            } catch (error) {
                console.error('Bulk cache storage failed:', error)
                return {
                    success: false,
                    totalEntries: entries.length,
                    storedEntries: 0,
                    memoryEntries: memoryUpdates,
                    skippedEntries,
                    error: error.message
                }
            }
        },

        async get(method, target, params = {}, payload = null) {
            const entry = await this.readEntry(this.generateKey(method, target, payload))
            if (!entry) return null

            if (!isFresh(entry)) {
                return navigator.onLine ? null : {
                    data: entry.data,
                    status: entry.status,
                    fromCache: true,
                    stale: true
                }
            }

            return {
                data: entry.data,
                status: entry.status,
                fromCache: true
            }
        },

        async has(method, target, params = {}, payload = null) {
            await this.init()
            const key = this.generateKey(method, target, payload)
            if (index.has(key)) return true
            if (this.indexComplete) return false
            return !!(await this.readEntry(key))
        },

        // L'index suffit à savoir si l'entrée est encore valide, sans lire ses données
        async hasValidTTL(method, target, params = {}, payload = null) {
            await this.init()
            const key = this.generateKey(method, target, payload)
            const meta = index.get(key)
            if (meta) return isFresh(meta)
            if (this.indexComplete) return false
            const entry = await this.readEntry(key)
            return !!entry && isFresh(entry)
        },

        async getValidTTLEntries(method, target, params = {}, payload = null) {
            await this.init()
            const key = this.generateKey(method, target, payload)
            const meta = index.get(key)
            if (meta && !isFresh(meta)) return null

            const entry = await this.readEntry(key)
            if (!entry || !isFresh(entry)) return null

            return {
                key,
                data: entry.data,
                status: entry.status,
                fromCache: true,
                stale: false
            }
        },

        async getStale(method, target, params = {}, payload = null) {
            const key = this.generateKey(method, target, payload)
            const entry = await this.readEntry(key)
            return entry ? {
                key,
                data: entry.data,
                status: entry.status,
                fromCache: true,
                stale: true
            } : null
        },

        async clear() {
            await this.init()
            generation++
            index.clear()
            recent.clear()
            this.indexComplete = true
            this.indexVersion++
            await storage.clear()
            this.resetSync()
            console.log('Cache cleared')
        },

        async updateCache() {
            await AppSyncService.syncCache()
        },

        async updateStorageInfo() {
            this.cachedStorageInfo = await storage?.getStorageInfo()
        }
    },

    getters: {
        getCacheStorageInfo: (state) => state?.cachedStorageInfo,

        // Une seule boucle sur les entrées (timestamp, ttl, method) : le cache synchronisé compte
        // des milliers d'entrées et des centaines de Mo, rien ne doit le sérialiser ni le parcourir deux fois
        getStats: (state) => {
            state.indexVersion // dépendance : l'index est hors de l'état réactif
            const now = Date.now()
            const byMethod = {}
            let totalEntries = 0
            let validEntries = 0

            for (const entry of index.values()) {
                const method = entry.method || 'UNKNOWN'
                const stats = byMethod[method] || (byMethod[method] = { total: 0, valid: 0, expired: 0 })
                totalEntries++
                stats.total++
                if (now - entry.timestamp <= entry.ttl) {
                    validEntries++
                    stats.valid++
                } else {
                    stats.expired++
                }
            }

            return {
                totalEntries,
                validEntries,
                expiredEntries: totalEntries - validEntries,
                hitRate: totalEntries > 0 ? Math.round((validEntries / totalEntries) * 100) : 0,
                byMethod,
                isInitialized: state.isInitialized,
                storageType: 'IndexedDB',
                storageStats: storage?.getStats() || {}
            }
        },

        getSyncState: (state) => state.sync,

        getSyncProgress: (state) => {
            const currentSpeed = state.sync.completedItems > 0 && state.sync.startTime
                ? Math.round(state.sync.completedItems / ((Date.now() - state.sync.startTime) / 1000))
                : 0

            return {
                progress: state.sync.progress,
                isActive: state.sync.isActive,
                message: state.sync.message,
                completedItems: state.sync.completedItems,
                totalItems: state.sync.totalItems,
                errors: state.sync.errors,
                status: state.sync.status,
                currentSpeed
            }
        },

        // Métadonnées seulement : les données restent dans IndexedDB
        getEntries: (state) => {
            state.indexVersion
            const now = Date.now()
            return Array.from(index, ([key, meta]) => {
                const age = now - meta.timestamp
                return {
                    key,
                    ...meta,
                    isExpired: age > meta.ttl,
                    age,
                    ageFormatted: `${Math.round(age / 1000)}s`
                }
            })
        },

        getExpiredEntries: (state) => {
            state.indexVersion
            const now = Date.now()
            return Array.from(index)
                .filter(([, meta]) => !isFresh(meta, now))
                .map(([key, meta]) => ({
                    key,
                    ...meta,
                    expiredSince: now - (meta.timestamp + meta.ttl)
                }))
        },

        getStorageInfo: () => ({
            pendingWrites: storage.pendingWritesCount || 0,
            hasPendingWrites: storage.hasPendingWrites || false,
            isFlushScheduled: storage.writeTimeout !== null,
            isFlushInProgress: storage.isFlushInProgress || false,
            storageStats: storage.getStats?.() || {}
        })
    }
})